import {
  type ChangeEvent,
  type ClipboardEvent,
  createContext,
  type DragEvent,
  type RefObject,
  useContext,
} from "react";
import type { AiChatUiContextType } from "@/lib/ai/ai-chat-ui-context";
import type { AnalysisTarget } from "@/lib/ai/analysis-target";
import type { ModelId } from "@/lib/ai/models";
import type { AnalysisTargetInlineMentionMetadata } from "./analysis-target-display";
import type { AnalysisTargetOptionsState } from "./analysis-target-options";
import type { ChatQuotaTierIndicatorState } from "./chat-quota-view-state";
import type {
  ChatSendAttachment,
  ChatSendFileValidationError,
} from "./chat-send-input";
import type { useAnalysisTargetMentionInput } from "./use-analysis-target-mention-input";

type AiChatSelectionContext = Pick<
  AiChatUiContextType,
  | "analysisTargets"
  | "pageAnalysisTarget"
  | "manualAnalysisTargets"
  | "addAnalysisTarget"
>;

type AnalysisTargetMentionInputState = ReturnType<
  typeof useAnalysisTargetMentionInput
>;

export type ChatPromptSurfaceContextValue = {
  input: string;
  setInput: (value: string) => void;
  isExpanded: boolean;
  canSubmit: boolean;
  isInteractive: boolean;
  isAttachmentDropActive: boolean;
  optionsState: AnalysisTargetOptionsState;
  analysisTargets: AiChatSelectionContext["analysisTargets"];
  pageAnalysisTarget: AiChatSelectionContext["pageAnalysisTarget"];
  manualAnalysisTargets: AiChatSelectionContext["manualAnalysisTargets"];
  addAnalysisTarget: AiChatSelectionContext["addAnalysisTarget"];
  mentionedTargets: AnalysisTargetMentionInputState["mentionedTargets"];
  mentionInstances: AnalysisTargetMentionInputState["mentionInstances"];
  mentionMatch: AnalysisTargetMentionInputState["mentionMatch"];
  mentionAnchor: AnalysisTargetMentionInputState["mentionAnchor"];
  mentionMenuOpen: boolean;
  mentionActiveOptionKey: AnalysisTargetMentionInputState["mentionActiveOptionKey"];
  setMentionActiveOptionKey: AnalysisTargetMentionInputState["setMentionActiveOptionKey"];
  handleMentionTargetSelect: AnalysisTargetMentionInputState["handleMentionTargetSelect"];
  handleTextareaChange: AnalysisTargetMentionInputState["handleTextareaChange"];
  handleTextareaKeyDown: AnalysisTargetMentionInputState["handleTextareaKeyDown"];
  handleTextareaKeyUp: AnalysisTargetMentionInputState["handleTextareaKeyUp"];
  handleTextareaClick: AnalysisTargetMentionInputState["handleTextareaClick"];
  textareaElement: AnalysisTargetMentionInputState["textareaElement"];
  bindTextarea: AnalysisTargetMentionInputState["bindTextarea"];
  optionMetadataByKey: ReadonlyMap<string, AnalysisTargetInlineMentionMetadata>;
  showInputOverlay: boolean;
  handleRemoveTarget: (target: AnalysisTarget) => void;
  handleSubmit: (msg: { text?: string }) => Promise<void>;
  attachments: ChatSendAttachment[];
  attachmentErrors: ChatSendFileValidationError[];
  fileInputId: string;
  attachmentInputRef: RefObject<HTMLInputElement | null>;
  handleAttachmentButtonClick: () => void;
  handleAttachmentChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleAttachmentDragEnter: (event: DragEvent<HTMLElement>) => void;
  handleAttachmentDragLeave: (event: DragEvent<HTMLElement>) => void;
  handleAttachmentDragOver: (event: DragEvent<HTMLElement>) => void;
  handleAttachmentDrop: (event: DragEvent<HTMLElement>) => void;
  handleAttachmentRemove: (attachmentId: string) => void;
  handleAttachmentErrorDismiss: (error: ChatSendFileValidationError) => void;
  handleAttachmentPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  textareaRows: number;
  model: ModelId;
  highQuotaIndicator: ChatQuotaTierIndicatorState;
  lowQuotaIndicator: ChatQuotaTierIndicatorState;
  setContextPickerOpen: (open: boolean) => void;
  setModelSelectOpen: (open: boolean) => void;
  handleModelValueChange: (model: ModelId) => void;
  /** ブラウザが音声入力（Web Speech API）に対応しているか。 */
  speechSupported: boolean;
  /** 音声入力の録音が進行中か。 */
  speechIsListening: boolean;
  /** 未確定の音声認識テキスト（仮入力表示に使用）。 */
  speechInterimTranscript: string;
  /** 音声入力の録音状態をトグルする。 */
  toggleSpeech: () => void;
};

export const ChatPromptSurfaceContext =
  createContext<ChatPromptSurfaceContextValue | null>(null);

/**
 * Reads the shared prompt surface state for components rendered under
 * `ChatPromptSurface`; throws when called outside that provider so misuse fails
 * before rendering with missing prompt handlers or state.
 */
export function useChatPromptSurfaceContext(componentName: string) {
  const value = useContext(ChatPromptSurfaceContext);
  if (!value) {
    throw new Error(
      `${componentName} must be rendered inside ChatPromptSurface`,
    );
  }
  return value;
}
